import { faCheck, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash';

import Checkbox from '../../core/components/Checkbox.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Table from '../../core/components/Table.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { PERMISSION_GROUPS, getRoleLabel, sortRoles } from '../utils.ts';
import type { IGroup } from '../../teams/interfaces';

const CheckIcon = withIcon(faCheck);
const InfoIcon = withIcon(faCircleInfo);

interface RolePermissionMatrixRole extends IGroup {
    disabled?: boolean;
}

interface RolePermissionMatrixProps {
    roles: RolePermissionMatrixRole[];
    value: string[];
    onValueChange: (value: string[]) => void;
}

const RolePermissionMatrix = ({ roles, value, onValueChange }: RolePermissionMatrixProps) => {
    const { t } = useTranslation('users');

    // column hover can't be expressed in CSS alone (columns span multiple rows)
    const [hoveredRoleId, setHoveredRoleId] = useState<string | null>(null);

    const sortedRoles = sortRoles(roles);

    return (
        <Table scrollable className="w-full table-auto">
            <Table.Head>
                <Table.Row>
                    <Table.HeadCell className="p-0 bg-surface-medium" />
                    {sortedRoles.map((role) => (
                        <Table.HeadCell
                            key={role.id}
                            className={classnames(
                                'p-0 text-center bg-surface-medium',
                                (hoveredRoleId === role.id || value.includes(role.id)) && 'bg-surface-strong'
                            )}
                            onMouseEnter={() => setHoveredRoleId(role.id)}
                            onMouseLeave={() => setHoveredRoleId(null)}
                        >
                            <label
                                className={classnames(
                                    'inline-flex flex-col items-center gap-2 p-2 pt-3 w-full rounded transition-colors min-w-20',
                                    role.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-strong'
                                )}
                            >
                                <Checkbox
                                    checked={value.includes(role.id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            onValueChange([...value, role.id]);
                                        } else {
                                            onValueChange(value.filter((v) => v !== role.id));
                                        }
                                    }}
                                    disabled={!!role.disabled}
                                />
                                <span className="text-content-strong">{getRoleLabel(t, role.name)}</span>
                            </label>
                        </Table.HeadCell>
                    ))}
                </Table.Row>
            </Table.Head>
            <Table.Body>
                {PERMISSION_GROUPS.map((group) => (
                    <React.Fragment key={group.key}>
                        <Table.Row>
                            <Table.Cell
                                colSpan={sortedRoles.length + 1}
                                className="h-auto py-1.5 text-xs font-medium text-content-medium bg-surface-medium"
                            >
                                {t(`form.permissionMatrix.groups.${group.key}`)}
                            </Table.Cell>
                        </Table.Row>
                        {group.permissions.map((permission) => {
                            const description = t(`form.permissionMatrix.descriptions.${camelCase(permission)}`);

                            return (
                                <Table.Row key={permission}>
                                    <Table.Cell className="h-auto py-2 font-medium text-content-medium">
                                        <span className="inline-flex items-center gap-1.5">
                                            {t(`form.permissionMatrix.capabilities.${camelCase(permission)}`)}
                                            <Tooltip content={description} side="top">
                                                <button
                                                    type="button"
                                                    aria-label={description}
                                                    className="flex rounded text-content-medium cursor-pointer"
                                                >
                                                    <InfoIcon className="w-3 h-3" />
                                                </button>
                                            </Tooltip>
                                        </span>
                                    </Table.Cell>
                                    {sortedRoles.map((role) => (
                                        <Table.Cell
                                            key={role.id}
                                            className={classnames(
                                                'h-auto px-2 py-2 text-center',
                                                role.disabled && 'opacity-50',
                                                (hoveredRoleId === role.id || value.includes(role.id)) &&
                                                    'bg-surface-medium'
                                            )}
                                            onMouseEnter={() => setHoveredRoleId(role.id)}
                                            onMouseLeave={() => setHoveredRoleId(null)}
                                        >
                                            {role.rolePermissions?.includes(permission) && (
                                                <CheckIcon className="text-content-brand-strong" />
                                            )}
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            );
                        })}
                    </React.Fragment>
                ))}
            </Table.Body>
        </Table>
    );
};

export default RolePermissionMatrix;
